import type { CultivarSalesProfile } from '../../types/cultivar';

/**
 * A cultivar's talking points, read-only: what somebody says to a customer.
 * Shared by the cultivar page and the nursery's quick view, so the two never
 * read differently.
 */

const FACTS: { key: 'fruit' | 'season' | 'tree' | 'growing'; label: string }[] = [
  { key: 'fruit', label: 'Fruit' },
  { key: 'season', label: 'Season' },
  { key: 'tree', label: 'Tree' },
  { key: 'growing', label: 'Growing' },
];

function Bullets({ title, items, tone }: { title: string; items: string[]; tone: 'good' | 'warn' }) {
  if (!items?.length) return null;
  return (
    <div className="min-w-0">
      <h4 className={`text-xs font-semibold uppercase tracking-wide ${tone === 'warn' ? 'text-amber-700' : 'text-primary-700'}`}>{title}</h4>
      <ul className="mt-1 space-y-1 text-sm text-neutral-800 list-disc pl-5">
        {items.map((item) => <li key={item} className="break-words">{item}</li>)}
      </ul>
    </div>
  );
}

export default function CultivarProfileView({ profile }: { profile: CultivarSalesProfile }) {
  const facts = FACTS.filter((f) => profile[f.key]);
  return (
    <div className="space-y-4 min-w-0">
      {profile.summary && <p className="text-base font-medium text-neutral-900 break-words">{profile.summary}</p>}

      {facts.length > 0 && (
        <dl className="space-y-2 text-sm">
          {facts.map((f) => (
            <div key={f.key} className="sm:flex sm:gap-4 min-w-0">
              <dt className="font-medium text-neutral-500 sm:w-20 sm:flex-shrink-0">{f.label}</dt>
              <dd className="text-neutral-800 break-words min-w-0">{profile[f.key]}</dd>
            </div>
          ))}
        </dl>
      )}

      {profile.best_for?.length > 0 && (
        <div className="min-w-0">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Best for</h4>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {profile.best_for.map((item) => (
              <li key={item} className="max-w-full rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs text-neutral-700 break-words">{item}</li>
            ))}
          </ul>
        </div>
      )}

      {(profile.selling_points?.length > 0 || profile.watch_outs?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Bullets title="Selling points" items={profile.selling_points} tone="good" />
          <Bullets title="Tell the customer" items={profile.watch_outs} tone="warn" />
        </div>
      )}

      {profile.compared_with?.length > 0 && (
        <div className="min-w-0">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">How it differs</h4>
          <ul className="mt-1.5 space-y-1.5 text-sm">
            {profile.compared_with.map((c) => (
              <li key={c.name} className="break-words">
                <span className="font-medium text-neutral-900">From {c.name}:</span>{' '}
                <span className="text-neutral-700">{c.difference}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
